import { TokenData } from '@/types/verification';
import { getEvaluatorById } from '@/services/evaluatorService';

const TOKEN_LENGTH = 64;
const TOKEN_EXPIRY = 30 * 60 * 1000; // 30 minutes
const STORAGE_KEY = 'evaluation_token_v1'; // Versioned key for future compatibility

interface StoredToken {
  token: string;
  data: TokenData;
  timestamp: number;
}

function generateSecureToken(): string {
  const array = new Uint8Array(TOKEN_LENGTH / 2);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

function storeToken(token: string, data: TokenData): void {
  try {
    // Remove any existing tokens first
    localStorage.removeItem(STORAGE_KEY);

    const tokenData: StoredToken = {
      token,
      data,
      timestamp: Date.now()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData));
  } catch (error) {
    console.error('Error storing token:', error);
    throw new Error('Failed to store access token');
  }
}

function getStoredToken(): { token: string; data: TokenData } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsedData = JSON.parse(stored) as StoredToken;
    
    // Validate stored data structure
    if (!parsedData.token || !parsedData.data || !parsedData.timestamp) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Check if token has expired
    if (Date.now() > parsedData.timestamp + TOKEN_EXPIRY) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Validate token format
    if (!/^[0-9a-f]{64}$/i.test(parsedData.token)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      token: parsedData.token,
      data: parsedData.data
    };
  } catch (error) {
    console.error('Error retrieving token:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export async function createToken(evaluatorId: string): Promise<string> {
  try {
    // Verify evaluator exists
    await getEvaluatorById(evaluatorId);
    
    // Generate new token
    const token = generateSecureToken();
    const expiresAt = Date.now() + TOKEN_EXPIRY;
    
    // Store token data
    const tokenData: TokenData = {
      evaluatorId,
      expiresAt
    };

    storeToken(token, tokenData);
    return token;
  } catch (error) {
    console.error('Error creating token:', error);
    throw new Error('Failed to create access token');
  }
}

export function validateToken(token: string): TokenData | null {
  if (!token || typeof token !== 'string') {
    console.warn('Invalid token format');
    return null;
  }

  const stored = getStoredToken();
  if (!stored || stored.token !== token) {
    console.warn('Token not found or mismatch');
    return null;
  }

  // Verify token hasn't expired
  if (stored.data.expiresAt < Date.now()) {
    invalidateToken(token);
    return null;
  }

  return stored.data;
}

export function invalidateToken(token: string): void {
  try {
    const stored = getStoredToken();
    if (stored && stored.token === token) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error invalidating token:', error);
  }
}

// Cleanup function to be called on logout
export function clearAllTokens(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
}